import {
	deriveTypes,
	generateDefaultUISchema,
	resolveSchema as resolveCoreSchema,
	toDataPath
} from "@jsonforms/core";

import type {
	ChoiceOption,
	FormUiSchema,
	JsonSchemaDefinition,
	ReferenceOptions
} from "./types";
import { isRecord } from "./values";

const generationSchemas = new WeakMap<JsonSchemaDefinition, JsonSchemaDefinition>();

export function schemaType(schema: JsonSchemaDefinition): string | undefined {
	const types = deriveTypes(schema);

	return types.find(type => type !== "null") ?? types[0];
}

export function referenceOptions(schema: JsonSchemaDefinition, ui: FormUiSchema): ReferenceOptions {
	const fallback = ui.options?.reference;

	return {
		configuration: schema.metadata?.configuration ?? fallback?.configuration,
		fieldCode: schema.metadata?.fieldCode ?? fallback?.fieldCode,
		modelProperty: schema.metadata?.modelProperty ?? fallback?.modelProperty
	};
}

export function resolveSchema(root: JsonSchemaDefinition, schema: JsonSchemaDefinition): JsonSchemaDefinition {
	return (resolveCoreSchema(schema, "#", root) ?? schema) as JsonSchemaDefinition;
}

export function schemaAtScope(
	root: JsonSchemaDefinition,
	scope: string,
	context: JsonSchemaDefinition = root
): JsonSchemaDefinition {
	return (resolveCoreSchema(context, scope, root) ?? {}) as JsonSchemaDefinition;
}

export function arrayItemSchema(root: JsonSchemaDefinition, schema: JsonSchemaDefinition, index: number): JsonSchemaDefinition {
	const items = schema.items;
	const item = Array.isArray(items) ? items[index] : items;
	const additional = typeof schema.additionalItems === "object" ? schema.additionalItems : {};

	return resolveSchema(root, item ?? additional);
}

export const controlPath = (scope: string, basePath = ""): string => [
	basePath,
	toDataPath(scope)
].filter(Boolean)
	.join(".");

export const pointerSegment = (key: string): string => key.replace(/~/g, "~0")
	.replace(/\//g, "~1");

/** The package generator skips untyped constants. Give only its presentation copy
 * a singleton choice so it emits a native Control for every constant JSON type.
 */
function schemaForGeneration(schema: JsonSchemaDefinition): JsonSchemaDefinition {
	if(!schema || typeof schema !== "object") {
		return schema;
	}

	const cached = generationSchemas.get(schema);

	if(cached) {
		return cached;
	}

	const source: Record<string, unknown> = { ...schema };
	const copy = { ...source };
	const projected = copy as JsonSchemaDefinition;
	generationSchemas.set(schema, projected);

	if(Object.prototype.hasOwnProperty.call(schema, "const") && !schema.oneOf?.length) {
		copy.oneOf = [{ const: schema.const }];
	}

	const project = (value: unknown): unknown =>
		isRecord(value) ? schemaForGeneration(value as JsonSchemaDefinition) : value;

	for(const key of ["properties", "definitions", "$defs", "patternProperties", "dependencies"]) {
		const children = source[key];

		if(isRecord(children)) {
			copy[key] = Object.fromEntries(Object.entries(children)
				.map(([name, child]) => [name, project(child)]));
		}
	}

	for(const key of ["allOf", "anyOf", "oneOf"]) {
		const children = source[key];

		if(Array.isArray(children)) {
			copy[key] = children.map(project);
		}
	}

	for(const key of ["items", "additionalItems", "additionalProperties", "contains", "propertyNames", "not", "if", "then", "else"]) {
		const child = source[key];

		if(child !== undefined) {
			copy[key] = Array.isArray(child) ? child.map(project) : project(child);
		}
	}

	return projected;
}

export function generatedUiSchema(
	schema: JsonSchemaDefinition,
	root: JsonSchemaDefinition = schema
): FormUiSchema {
	return generateDefaultUISchema(schemaForGeneration(schema), "VerticalLayout", "#", schemaForGeneration(root)) as FormUiSchema ?? {
		type: "VerticalLayout",
		elements: []
	};
}

/** Generate the contents of an object card without dispatching its own combinator again. */
export function generatedObjectUiSchema(schema: JsonSchemaDefinition, root: JsonSchemaDefinition): FormUiSchema {
	const resolved = resolveSchema(root, schema);
	const contents: JsonSchemaDefinition = {
		type: "object",
		properties: resolved.properties
	};

	return generatedUiSchema(contents, root);
}

export function getUiSchema(schema: JsonSchemaDefinition): FormUiSchema {
	return schema.metadata?.form ?? generatedUiSchema(schema);
}

export function enumOptions(schema: JsonSchemaDefinition): ChoiceOption[] {
	if(Object.prototype.hasOwnProperty.call(schema, "const")) {
		return [{
			value: schema.const,
			label: schema.title ?? String(schema.const)
		}];
	}

	if(schema.enum) {
		return schema.enum.map(value => ({
			value,
			label: String(value)
		}));
	}

	const choices = schema.oneOf ?? [];

	if(!choices.every(option => Object.prototype.hasOwnProperty.call(option, "const"))) {
		return [];
	}

	return choices
		.map(option => ({
			value: option.const,
			label: option.title ?? String(option.const)
		}));
}
